/**
 * ADR-118 — React `<AuthProvider>` + hooks.
 *
 * Wraps `libs/auth-core` primitives in a single React surface.
 * Consumers (AIM / SMS / ONE / AIR / Beacon) replace their per-app
 * AuthContext with this provider; auth state, login/logout actions,
 * cross-tab sync, boot-time reconciliation, and WS close-on-logout
 * all come bundled.
 *
 * The provider is **generic** over the user shape `U` so each app
 * threads its own user type through to consumers without `unknown`
 * casts. ONE: `{id, email, first_name, last_name}`. AIM/SMS/AIR:
 * the richer shape with schools, vendor, etc.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  authReducer,
  bumpHeartbeat,
  clearAuthCookie,
  createLocalStorageAdapter,
  fireWebSocketClose,
  initialAuthState,
  reconcile,
  registerWebSocketCloseHook,
  StorageKeys,
  subscribeCrossTab,
  type AuthState,
  type BaseUser,
  type StorageAdapter,
  type LogoutResult,
} from '@aegix/auth-core';
import type {
  LoginAdapter,
  RefreshAdapter,
  LoginCredentials,
  OAuthCredentials,
  SignupCredentials,
  RequiresVerification,
} from './adapters/types';

/**
 * The shape exposed by `useAuth()` to consumers. Most code reaches
 * for a more specific hook (`useUser`, `useIsAuthenticated`,
 * `useAuthActions`, `useIsHydrated`) — `useAuth` is the escape hatch
 * for code that needs the raw state object.
 */
export interface AuthContextValue<U extends BaseUser> {
  state: AuthState<U>;
  actions: AuthActions<U>;
  /**
   * `false` until boot-time reconciliation has dispatched
   * its first `hydrate`. Apps that render based on `isAuthenticated`
   * should suppress UI (e.g. show a spinner) until this flips to
   * avoid a brief flash of the unauthenticated route between the
   * initial `'anonymous'` state and the reconciled state.
   *
   * For Branch-2 (cookie present, no localStorage), this stays
   * `false` until `fetchMe` resolves (or rejects) — i.e. the spinner
   * persists across the network round-trip.
   */
  isHydrated: boolean;
}

/**
 * The action functions available to consumers. Methods that the
 * adapter doesn't implement are `undefined` — apps without OAuth
 * never see `loginWithOAuth` defined, so a misuse is a TS error
 * rather than a runtime no-op.
 */
export interface AuthActions<U extends BaseUser> {
  login(credentials: LoginCredentials): Promise<void>;
  loginWithToken?: (jwt: string) => Promise<void>;
  loginWithOAuth?: (credentials: OAuthCredentials) => Promise<void>;
  loginWithPasskey?: () => Promise<void>;
  signup?: (
    credentials: SignupCredentials,
  ) => Promise<{ ok: true } | RequiresVerification>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  /**
   * Replace the user object on the current state without
   * touching the token or status. Used by `libs/auth-permissions` to
   * push a freshly-fetched user (with updated ``permissions``) after
   * the backend bumps ``permissions_version``. No-op when the state
   * has no user (anonymous / authenticating / logged-out).
   */
  updateUser(user: U): void;
  /**
   * Sync user language preference to the backend and update the local auth state.
   */
  updateLanguage(language: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue<BaseUser> | null>(null);

export interface AuthProviderProps<U extends BaseUser> {
  /** The login adapter (simpleJwtAdapter for AIM/SMS/AIR, allauthAdapter for ONE). */
  loginAdapter: LoginAdapter<U>;
  /** Optional refresh adapter. If absent, no auto-refresh. */
  refreshAdapter?: RefreshAdapter;
  /**
   * Storage adapter override. Defaults to `createLocalStorageAdapter()`.
   * Tests pass `createMemoryStorageAdapter()`.
   */
  storage?: StorageAdapter;
  /**
   * Cookie name for boot-time reconciliation. Defaults to
   * `'jwt-auth'`. Apps using a non-standard name override here.
   */
  cookieName?: string;
  /**
   * Storage/cookie keys that hold the user's locale preference.
   * Defaults to `['i18nextLng']`.
   */
  localeStorageKeys?: string[];
  /**
   * Called after the provider's logout flow completes (server-side
   * logout dispatched, localStorage cleared, cookie cleared, WS
   * close fired). Useful for navigation (e.g. `() => navigate('/login')`).
   */
  onLogout?: () => void;
  /**
   * WebSocket close callback. Registered with `auth-core`'s WS
   * close hook on mount; called with close code 4001 during logout.
   * Apps without a WS connection (some onboarding flows, ONE today)
   * omit this.
   */
  onWebSocketClose?: (code: number) => void;
  children: ReactNode;
}

const DEFAULT_LOCALE_STORAGE_KEYS = ['i18nextLng'];

export function AuthProvider<U extends BaseUser>({
  loginAdapter,
  refreshAdapter,
  storage,
  cookieName = 'jwt-auth',
  localeStorageKeys = DEFAULT_LOCALE_STORAGE_KEYS,
  onLogout,
  onWebSocketClose,
  children,
}: AuthProviderProps<U>) {
  // Memoize the storage adapter so we don't recreate it every render
  // (and to give consumers a way to inject test-mocks via the prop).
  const storageRef = useRef<StorageAdapter>(
    storage ?? createLocalStorageAdapter(),
  );

  const [state, dispatch] = useReducer(
    authReducer as (
      s: AuthState<U>,
      a: ReturnType<typeof authReducer<U>> extends AuthState<U>
        ? Parameters<typeof authReducer<U>>[1]
        : never,
    ) => AuthState<U>,
    initialAuthState as AuthState<U>,
  );

  // Hydration tracking — flips to `true` once boot-time
  // reconciliation has either completed synchronously (Branch 1/3/4)
  // or finished its async `fetchMe` probe (Branch 2). Apps consume
  // this via `useIsHydrated()` to suppress flash-of-unauth UI.
  const [isHydrated, setIsHydrated] = useState(false);

  // Persistence side-effect: keep storage in sync with state.
  // The reducer is pure; persistence lives in this effect.
  //
  // Do NOTHING until boot reconcile has run (`isHydrated`).
  // This effect is declared before the reconcile effect, so on mount it
  // fires first — with the initial `anonymous` state — and a
  // clear-branch here would `clearAuthKeys()` and WIPE the persisted
  // token/user/refresh *before* reconcile reads them, logging the user
  // out on every reload. The boot reconcile owns the initial storage
  // read; this effect only syncs state→storage once hydration is done.
  useEffect(() => {
    if (!isHydrated) return;
    const s = storageRef.current;
    if (state.status === 'authenticated' || state.status === 'refreshing') {
      s.set(StorageKeys.token, state.jwt);
      s.set(StorageKeys.user, state.user);
      if (state.refreshToken) {
        s.set(StorageKeys.refreshToken, state.refreshToken);
      }
    }
    // This effect only WRITES (authenticated/refreshing → storage).
    // It must NOT clear storage as a side-effect of entering a non-authed
    // state. `logged-out`/`anonymous` can be transient or boot-originated (a
    // failed cross-app boot probe), and a `clearAuthKeys()` here would wipe
    // the localStorage refresh token — which is shared across same-origin
    // tabs and broadcast cross-tab — so one failed boot would strand every
    // session. Storage clearing is explicit-logout only: the `logout()`
    // action does its own `clearAuthCookie` + `clearAuthKeys`. `expired`
    // likewise preserves storage (the re-auth banner needs to know who
    // expired).
  }, [state, isHydrated]);

  // Boot-time reconciliation.
  useEffect(() => {
    const {
      state: reconciled,
      requiresProbe,
      hasLocalCredential,
    } = reconcile<U>(storageRef.current, {
      cookieName,
      // The parent-domain SSO assertion is HttpOnly, so
      // reconcile cannot detect it — it has to be told the adapter can
      // trade it for a session, so a virgin origin still probes
      // instead of settling on `anonymous` (which would make the SSO
      // bootstrap below unreachable on its intended first-landing).
      ssoBootstrapAvailable: Boolean(loginAdapter.bootstrapFromSso),
    });
    if (!requiresProbe) {
      dispatch({ type: 'hydrate', state: reconciled });
      setIsHydrated(true);
      return;
    }
    // Branch 2: we hold a credential to validate. Resolve it, then probe /me/.
    dispatch({ type: 'hydrate', state: reconciled });
    void (async () => {
      try {
        const s = storageRef.current;
        const rawToken = s.get<string>(StorageKeys.token);
        // Never treat the `__cookie__` sentinel as a token.
        let accessToken = rawToken && rawToken !== '__cookie__' ? rawToken : '';
        const storedRefresh =
          s.get<string>(StorageKeys.refreshToken) ?? undefined;

        // Boot-time refresh: the mobile pattern keeps the access
        // token ephemeral (sessionStorage), so on a fresh load it's often
        // gone while the refresh token (localStorage) persists. If we have
        // no usable access token but a refresh token + a refresh adapter,
        // mint a fresh access token here instead of bouncing to login —
        // this is what makes the ephemeral-access pattern survive reload
        // without depending on a JS-readable cookie.
        if (!accessToken && storedRefresh && refreshAdapter) {
          try {
            const refreshed = await refreshAdapter.refresh(storedRefresh);
            accessToken = refreshed.jwt;
            s.set(StorageKeys.token, accessToken);
          } catch {
            // Refresh token dead/expired — fall through to the cookie
            // probe (fetchMe with no token); if that 401s we log out.
          }
        }

        // ADR-118 Phase 2: silent cross-app SSO bootstrap. On a genuine
        // first-landing — no usable access token AND no stored refresh
        // token, so nothing local to anchor on — trade the parent-domain,
        // HttpOnly `aegix-sso` assertion (sent via credentials:'include') for a
        // full LoginResult INCLUDING a per-app refresh token. Runs BETWEEN the
        // boot-refresh (which needs a stored refresh token) and the cookie-probe
        // below, replacing the identity-ambiguous cookie-probe fallback (the
        // wrong-identity edge — booting as whoever last wrote the shared
        // cookie) with a correct-identity SSO handoff. On any failure (no
        // assertion / expired / endpoint absent) fall through to the
        // non-destructive cookie-probe — there's nothing stored here to clear.
        if (!accessToken && !storedRefresh && loginAdapter.bootstrapFromSso) {
          try {
            const result = await loginAdapter.bootstrapFromSso();
            s.set(StorageKeys.token, result.jwt);
            if (result.refreshToken) {
              s.set(StorageKeys.refreshToken, result.refreshToken);
            }
            dispatch({
              type: 'login_success',
              user: result.user,
              jwt: result.jwt,
              refreshToken: result.refreshToken,
            });
            return;
          } catch {
            // On the bootstrap-only path there is no
            // locally-detectable credential — reconcile probed solely
            // because the adapter *might* hold an HttpOnly SSO
            // assertion. A failed bootstrap is the end of the line: the
            // cookie-probe below has nothing to ride, so a genuinely
            // anonymous first visit pays one cheap failed POST rather
            // than two round-trips before /login renders. Storage stays
            // untouched; the `finally` still sets hydrated.
            if (!hasLocalCredential) {
              dispatch({ type: 'logout' });
              return;
            }
            // Fall through to the cookie-probe.
          }
        }

        // Send the access token as the Bearer credential; an
        // empty token sends no header and the backend uses the cookie
        // (cookie-only path).
        const user = await loginAdapter.fetchMe(accessToken || undefined);
        dispatch({
          type: 'login_success',
          user,
          jwt: accessToken,
          refreshToken: storedRefresh,
        });
      } catch {
        // Boot probe failed — go logged-out for THIS load, but do
        // NOT destroy stored credentials or the shared cookie. The failure
        // can be transient (network) or app-specific (a cross-app load whose
        // origin can't see the same-origin cookie). Wiping here would nuke the
        // localStorage refresh token — shared across same-origin tabs and
        // broadcast cross-tab — permanently stranding every session on one
        // failed boot. Preserve credentials so the next load/retry can
        // recover; clearing is reserved for the explicit `logout()` action
        // (and the lazy-401 path handles a genuinely dead token in-session).
        dispatch({ type: 'logout' });
      } finally {
        setIsHydrated(true);
      }
    })();
    // Mount-only by design: boot reconciliation runs once. No
    // `react-hooks/exhaustive-deps` disable here — the react-hooks plugin is
    // not loaded for this lib's config, so the directive would reference an
    // undefined rule (ESLint error) and suppress nothing.
  }, []);

  // Cross-tab sync.
  useEffect(() => {
    return subscribeCrossTab((event) => {
      if (event.kind === 'logout') {
        dispatch({ type: 'logout' });
      } else if (event.kind === 'login' || event.kind === 'token-changed') {
        // Re-hydrate from storage — another tab updated state.
        // `ssoBootstrapAvailable` is deliberately omitted here —
        // this handler re-hydrates synchronously without running probes, so
        // flipping an empty-storage result to 'authenticating' would strand
        // the UI in a spinner with nothing coming to resolve it.
        const { state: reconciled } = reconcile<U>(storageRef.current, {
          cookieName,
        });
        dispatch({ type: 'hydrate', state: reconciled });
      }
    });
  }, [cookieName]);

  // Discriminated union narrowing for the dependency array
  const authedUser = state.status === 'authenticated' ? state.user : null;
  const authedJwt = state.status === 'authenticated' ? state.jwt : null;

  // Language reconciliation
  useEffect(() => {
    if (!authedUser) return;

    let localLang: string | null = null;
    if (typeof localStorage !== 'undefined') {
      for (const key of localeStorageKeys) {
        const val = localStorage.getItem(key);
        if (val) {
          localLang = val;
          break;
        }
      }
    }

    const cleanLocalLang = localLang ? localLang.split('-')[0] : null;
    const cleanUserLang = authedUser.language
      ? authedUser.language.split('-')[0]
      : null;

    if (cleanLocalLang && cleanLocalLang !== cleanUserLang) {
      // Adopt the local pre-auth pick to the backend account
      if (loginAdapter.updateLanguage && authedUser.id) {
        void loginAdapter
          .updateLanguage(authedUser.id, cleanLocalLang, authedJwt)
          .then((updatedUser) => {
            dispatch({
              type: 'set_user',
              user: {
                ...authedUser,
                ...updatedUser,
                language: cleanLocalLang,
              },
            });
          })
          .catch((err) => {
            console.error(
              'Failed to reconcile language preference to backend:',
              err,
            );
          });
      }
    } else if (cleanUserLang && cleanUserLang !== cleanLocalLang) {
      // We read and write directly to localStorage/cookies instead of using the storage adapter
      // because i18next and other components expect raw strings (e.g. 'fr' or 'en')
      // rather than the JSON-quoted strings (e.g. '"fr"') produced by the storage adapter.
      if (typeof localStorage !== 'undefined') {
        for (const key of localeStorageKeys) {
          localStorage.setItem(key, cleanUserLang);
        }
      }
      if (typeof document !== 'undefined') {
        // `Secure` is correct for every deployed environment (all
        // HTTPS) and for the standard Vite dev server, because browsers treat
        // `http://localhost` as a trustworthy origin and accept `Secure` from
        // it. It is NOT accepted from a plain-http origin on any other host —
        // a LAN IP or a bare hostname when testing from a phone. There the
        // cookie is dropped silently: no console warning, no error. Since the
        // backend reads this cookie for server-side language selection, the
        // symptom is "language doesn't stick" with nothing to grep for. Serve
        // such an origin over HTTPS rather than dropping `Secure`.
        for (const key of localeStorageKeys) {
          document.cookie = `${key}=${cleanUserLang}; path=/; max-age=31536000; SameSite=Lax; Secure`;
        }
      }
    }
  }, [authedUser, authedJwt, loginAdapter, localeStorageKeys]);

  // WS close hook. Register on mount; unregister on unmount. The fire
  // happens inside `logout()` below.
  useEffect(() => {
    if (!onWebSocketClose) return;
    return registerWebSocketCloseHook(onWebSocketClose);
  }, [onWebSocketClose]);

  // Login action — wraps the adapter call, dispatches reducer.
  const login = useCallback(
    async (credentials: LoginCredentials) => {
      dispatch({ type: 'login_start' });
      try {
        const result = await loginAdapter.login(credentials);
        dispatch({
          type: 'login_success',
          user: result.user,
          jwt: result.jwt,
          refreshToken: result.refreshToken,
        });
        bumpHeartbeat();
      } catch (err) {
        dispatch({ type: 'login_failure' });
        throw err;
      }
    },
    [loginAdapter],
  );

  // Logout action — fires WS close, calls adapter (server-side
  // authoritative clear), clears cookie + storage, dispatches.
  const logout = useCallback(async () => {
    fireWebSocketClose(); // before storage clear so the WS client
    // can still read the token to identify itself in any farewell.
    const tokenForServerCall =
      state.status === 'authenticated' || state.status === 'refreshing'
        ? state.jwt
        : null;
    try {
      await loginAdapter.logout(tokenForServerCall);
    } catch (err) {
      // Server-side logout failure shouldn't block the client
      // cleanup. The cookie-clear endpoint emits Set-Cookie even on
      // some error paths; worst case the cookie outlives this call
      // by a few minutes until natural expiry.
      // eslint-disable-next-line no-console
      console.warn(
        '[auth-identity] adapter.logout threw — continuing with client cleanup',
        err,
      );
    }
    clearAuthCookie({ name: cookieName });
    storageRef.current.clearAuthKeys();
    dispatch({ type: 'logout' });
    bumpHeartbeat();
    onLogout?.();
  }, [loginAdapter, cookieName, onLogout, state]);

  // Refresh action — only meaningful if a refreshAdapter was supplied.
  const refresh = useCallback(async () => {
    if (!refreshAdapter) {
      throw new Error(
        '[auth-identity] refresh() called but no refreshAdapter was supplied to <AuthProvider>',
      );
    }
    if (state.status !== 'authenticated') return;
    if (!state.refreshToken) {
      // No refresh token to use — escalate to expired.
      dispatch({ type: 'expire' });
      return;
    }
    dispatch({ type: 'refresh_start' });
    try {
      const result = await refreshAdapter.refresh(state.refreshToken);
      dispatch({ type: 'refresh_success', jwt: result.jwt });
    } catch (err) {
      dispatch({ type: 'refresh_failure' });
      throw err;
    }
  }, [refreshAdapter, state]);

  // Optional login methods — only defined if the adapter supports them.
  const loginWithToken = useMemo<AuthActions<U>['loginWithToken']>(() => {
    if (!loginAdapter.loginWithToken) return undefined;
    return async (jwt: string) => {
      dispatch({ type: 'login_start' });
      try {
        const result = await loginAdapter.loginWithToken!(jwt);
        dispatch({
          type: 'login_success',
          user: result.user,
          jwt: result.jwt,
          refreshToken: result.refreshToken,
        });
        bumpHeartbeat();
      } catch (err) {
        dispatch({ type: 'login_failure' });
        throw err;
      }
    };
  }, [loginAdapter]);

  const loginWithOAuth = useMemo<AuthActions<U>['loginWithOAuth']>(() => {
    if (!loginAdapter.loginWithOAuth) return undefined;
    return async (credentials: OAuthCredentials) => {
      dispatch({ type: 'login_start' });
      try {
        const result = await loginAdapter.loginWithOAuth!(credentials);
        dispatch({
          type: 'login_success',
          user: result.user,
          jwt: result.jwt,
          refreshToken: result.refreshToken,
        });
        bumpHeartbeat();
      } catch (err) {
        dispatch({ type: 'login_failure' });
        throw err;
      }
    };
  }, [loginAdapter]);

  const loginWithPasskey = useMemo<AuthActions<U>['loginWithPasskey']>(() => {
    if (!loginAdapter.loginWithPasskey) return undefined;
    return async () => {
      dispatch({ type: 'login_start' });
      try {
        const result = await loginAdapter.loginWithPasskey!();
        dispatch({
          type: 'login_success',
          user: result.user,
          jwt: result.jwt,
          refreshToken: result.refreshToken,
        });
        bumpHeartbeat();
      } catch (err) {
        dispatch({ type: 'login_failure' });
        throw err;
      }
    };
  }, [loginAdapter]);

  const signup = useMemo<AuthActions<U>['signup']>(() => {
    if (!loginAdapter.signup) return undefined;
    return async (credentials: SignupCredentials) => {
      const result = await loginAdapter.signup!(credentials);
      if ('requiresVerification' in result) {
        return result;
      }
      // Direct-login signup (no verification needed)
      dispatch({
        type: 'login_success',
        user: result.user,
        jwt: result.jwt,
        refreshToken: result.refreshToken,
      });
      bumpHeartbeat();
      return { ok: true } as const;
    };
  }, [loginAdapter]);

  // Stable callback that dispatches `set_user`. Used by
  // `libs/auth-permissions` after a permissions_version bump to push
  // the freshly fetched user (with updated ``permissions``) into
  // provider state without rotating the token.
  const updateUser = useCallback((user: U) => {
    dispatch({ type: 'set_user', user });
  }, []);

  const updateLanguage = useCallback(
    async (language: string) => {
      if (!authedUser) return;
      if (!loginAdapter.updateLanguage) {
        console.warn('loginAdapter does not support updateLanguage');
        return;
      }
      const updatedUser = await loginAdapter.updateLanguage(
        authedUser.id,
        language,
        authedJwt,
      );
      // Merge, don't replace. `set_user` is a wholesale swap that also persists
      // to storage, and the api/v2 `UserSerializer` this PATCH returns is
      // narrower than the login/me payload — it omits `schools`, `vendor` and
      // `is_vendor_user`, which downstream consumers (useUserData,
      // chatLeftSideBar, auth-organization) read. Dispatching the raw response
      // would silently strip them. Mirrors the reconcile branch above.
      if (typeof localStorage !== 'undefined') {
        for (const key of localeStorageKeys) {
          localStorage.setItem(key, language);
        }
      }
      if (typeof document !== 'undefined') {
        // See the note on the reconciliation effect above: `Secure` is dropped
        // silently on a plain-http non-localhost dev origin.
        for (const key of localeStorageKeys) {
          document.cookie = `${key}=${language}; path=/; max-age=31536000; SameSite=Lax; Secure`;
        }
      }
      dispatch({
        type: 'set_user',
        user: { ...authedUser, ...updatedUser, language },
      });
    },
    [authedUser, authedJwt, loginAdapter, localeStorageKeys],
  );

  const actions: AuthActions<U> = useMemo(
    () => ({
      login,
      logout,
      refresh,
      updateUser,
      updateLanguage,
      loginWithToken,
      loginWithOAuth,
      loginWithPasskey,
      signup,
    }),
    [
      login,
      logout,
      refresh,
      updateUser,
      updateLanguage,
      loginWithToken,
      loginWithOAuth,
      loginWithPasskey,
      signup,
    ],
  );

  const value = useMemo<AuthContextValue<U>>(
    () => ({ state, actions, isHydrated }),
    [state, actions, isHydrated],
  );

  return (
    <AuthContext.Provider value={value as AuthContextValue<BaseUser>}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Escape-hatch hook returning the raw state + actions. Prefer
 * `useUser` / `useIsAuthenticated` / `useAuthActions` for typical
 * consumer code.
 */
export function useAuth<U extends BaseUser>(): AuthContextValue<U> {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('[auth-identity] useAuth called outside <AuthProvider>');
  }
  return ctx as AuthContextValue<U>;
}

/**
 * Returns the user object when authenticated / refreshing / expired,
 * `null` when anonymous / authenticating / logged-out.
 *
 * Note: an `expired` user is still returned (so the UI can render
 * a "your session ended, please re-auth" banner with their name).
 * Consumers that need to gate on "truly authenticated" should use
 * `useIsAuthenticated()` instead.
 */
export function useUser<U extends BaseUser>(): U | null {
  const { state } = useAuth<U>();
  switch (state.status) {
    case 'authenticated':
    case 'refreshing':
    case 'expired':
      return state.user;
    default:
      return null;
  }
}

/**
 * Strict authentication check. Returns true only when state is
 * `authenticated`. `refreshing` returns true (token is just being
 * rotated; user is still authenticated). `expired` returns false
 * (user identity is known but credentials are gone).
 */
export function useIsAuthenticated(): boolean {
  const { state } = useAuth();
  return state.status === 'authenticated' || state.status === 'refreshing';
}

/**
 * Action hook — login, logout, refresh, plus the optional
 * login methods if the adapter supports them.
 */
export function useAuthActions<U extends BaseUser>(): AuthActions<U> {
  const { actions } = useAuth<U>();
  return actions;
}

/**
 * Returns `true` once boot-time reconciliation has completed (both
 * synchronous Branch 1/3/4 hydration AND the async Branch-2 probe).
 * `false` during the first render(s) before `useEffect` runs, and
 * across the fetchMe round-trip for Branch 2.
 *
 * Apps that gate routes on `useIsAuthenticated()` should render a
 * spinner while `!isHydrated` to avoid a flash of the
 * unauthenticated route on hard refresh.
 */
export function useIsHydrated(): boolean {
  const { isHydrated } = useAuth<BaseUser>();
  return isHydrated;
}
